import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCMS } from "@/hooks/useCMS";
import { useAuth } from "@/context/AuthContext";
import { MediaPicker } from "@/components/MediaPicker";

const FIELDS = [
  { name: "contact_email", label: "Contact email" },
  { name: "mission_line", label: "Mission line" },
  { name: "copyright", label: "Copyright" },
  { name: "location", label: "Location" },
  { name: "marquee_words", label: "Marquee words (· separated)", area: true },
  { name: "launch_badge", label: "Launch badge text" },
  { name: "launch_date", label: "Launch date" },
  { name: "social_instagram", label: "Instagram URL" },
  { name: "social_facebook", label: "Facebook URL" },
  { name: "social_youtube", label: "YouTube URL" },
  { name: "social_tiktok", label: "TikTok URL" },
];

export default function SettingsPage() {
  const { reload } = useCMS();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Settings saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>;

  return (
    <div className="max-w-2xl" data-testid="admin-settings">
      <h1 className="font-display font-black uppercase text-3xl">Site Settings</h1>
      <p className="mt-1 text-muted-foreground text-sm">Global content used across the site header, footer and marquee.</p>
      {!isAdmin && <p className="mt-4 rounded-xl bg-muted p-3 text-sm text-muted-foreground">Only admins can change site settings.</p>}

      <div className="mt-8 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="settings-logo">
          <label className="block text-sm font-semibold mb-1.5">Site logo</label>
          <p className="mb-3 text-xs text-muted-foreground">Appears in the site header, admin login and branded emails. Upload a transparent PNG for best results.</p>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-40 items-center justify-center overflow-hidden rounded-xl border border-border bg-[#0F172A] px-3">
              {form.logo ? <img src={form.logo} alt="Logo preview" className="max-h-12 w-auto object-contain" data-testid="settings-logo-preview" /> : <span className="text-xs text-white/50">No logo</span>}
            </div>
            <div className="flex-1">
              <input disabled={!isAdmin} value={form.logo || ""} onChange={(e) => setForm((s) => ({ ...s, logo: e.target.value }))} placeholder="https://…" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid="setting-logo" />
              {isAdmin && <MediaPicker onPick={(url) => setForm((s) => ({ ...s, logo: url }))} />}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="settings-logo-light">
          <label className="block text-sm font-semibold mb-1.5">Logo — light version (for dark backgrounds)</label>
          <p className="mb-3 text-xs text-muted-foreground">Used on the admin login panel and branded email header (both dark). Falls back to the main logo if left empty.</p>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-40 items-center justify-center overflow-hidden rounded-xl border border-border bg-[#0F172A] px-3">
              {form.logo_light ? <img src={form.logo_light} alt="Light logo preview" className="max-h-12 w-auto object-contain" data-testid="settings-logo-light-preview" /> : <span className="text-xs text-white/50">Uses main logo</span>}
            </div>
            <div className="flex-1">
              <input disabled={!isAdmin} value={form.logo_light || ""} onChange={(e) => setForm((s) => ({ ...s, logo_light: e.target.value }))} placeholder="https://…" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid="setting-logo_light" />
              {isAdmin && <MediaPicker onPick={(url) => setForm((s) => ({ ...s, logo_light: url }))} />}
            </div>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold mb-1.5">Logo size</label>
            <select disabled={!isAdmin} value={form.logo_size || "medium"} onChange={(e) => setForm((s) => ({ ...s, logo_size: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid="setting-logo_size">
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5">Site density</label>
            <select disabled={!isAdmin} value={form.density || "comfortable"} onChange={(e) => setForm((s) => ({ ...s, density: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid="setting-density">
              <option value="compact">Compact</option>
              <option value="cozy">Cozy</option>
              <option value="comfortable">Comfortable</option>
              <option value="spacious">Spacious</option>
            </select>
          </div>
        </div>
        {FIELDS.map((f) => (
          <div key={f.name}>
            <label className="block text-sm font-semibold mb-1.5">{f.label}</label>
            {f.area ? (
              <textarea rows={2} disabled={!isAdmin} value={form[f.name] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid={`setting-${f.name}`} />
            ) : (
              <input disabled={!isAdmin} value={form[f.name] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid={`setting-${f.name}`} />
            )}
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-border bg-card p-5" data-testid="settings-blog">
        <h2 className="font-display font-black uppercase text-lg">Blog / Articles</h2>
        <p className="mt-1 text-sm text-muted-foreground">Control the blog page and where articles appear.</p>
        <div className="mt-5 space-y-4">
          {[
            { name: "blog_title", label: "Blog title" },
            { name: "blog_title_fr", label: "Blog title (FR)" },
            { name: "blog_intro", label: "Blog intro", area: true },
            { name: "blog_intro_fr", label: "Blog intro (FR)", area: true },
          ].map((f) => (
            <div key={f.name}>
              <label className="block text-sm font-semibold mb-1.5">{f.label}</label>
              {f.area ? (
                <textarea rows={2} disabled={!isAdmin} value={form[f.name] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid={`setting-${f.name}`} />
              ) : (
                <input disabled={!isAdmin} value={form[f.name] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid={`setting-${f.name}`} />
              )}
            </div>
          ))}
          <div>
            <label className="block text-sm font-semibold mb-1.5">Articles per row (blog page)</label>
            <select disabled={!isAdmin} value={form.articles_per_row || "3"} onChange={(e) => setForm((s) => ({ ...s, articles_per_row: e.target.value }))} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60" data-testid="setting-articles_per_row">
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
            </select>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" disabled={!isAdmin} checked={form.show_blog_nav !== false} onChange={(e) => setForm((s) => ({ ...s, show_blog_nav: e.target.checked }))} className="h-5 w-5 rounded accent-[#2CACD4] cursor-pointer" data-testid="setting-show_blog_nav" />
            <span className="font-semibold text-sm">Show "Blog" in the site navigation</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" disabled={!isAdmin} checked={form.show_blog_home !== false} onChange={(e) => setForm((s) => ({ ...s, show_blog_home: e.target.checked }))} className="h-5 w-5 rounded accent-[#2CACD4] cursor-pointer" data-testid="setting-show_blog_home" />
            <span className="font-semibold text-sm">Show featured stories on the home page</span>
          </label>
        </div>
      </div>

      {isAdmin && (
        <button onClick={save} disabled={saving} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-3 transition-colors cursor-pointer disabled:opacity-60" data-testid="settings-save">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> Save settings</>}
        </button>
      )}
    </div>
  );
}
